import type { TimelineAnnotation } from "./AnnotationTimeline";

export function clampTimelineSeekSeconds(
  timeSeconds: number,
  totalDuration: number,
): number {
  const safeDuration = Math.max(0, totalDuration);
  if (!Number.isFinite(timeSeconds)) {
    return 0;
  }
  return Math.max(0, Math.min(safeDuration, timeSeconds));
}

export function clampTimelinePageStart(
  pageStart: number,
  pageDuration: number,
  totalDuration: number,
): number {
  const safePageDuration = Math.max(0.1, pageDuration);
  const safeTotal = Math.max(safePageDuration, totalDuration);
  const maxStart = Math.max(0, safeTotal - safePageDuration);
  return clampTimelineSeekSeconds(pageStart, maxStart);
}

export type TimelinePointerTarget = "page-marker" | "timeline-background";

export function hitTestTimelinePageMarker(params: {
  pointerX: number;
  pageStartX: number;
  pageEndX: number;
  hitPaddingPx?: number;
}): TimelinePointerTarget {
  const padding = Math.max(0, params.hitPaddingPx ?? 2);
  const left = Math.min(params.pageStartX, params.pageEndX) - padding;
  const right = Math.max(params.pageStartX, params.pageEndX) + padding;
  if (params.pointerX >= left && params.pointerX <= right) {
    return "page-marker";
  }
  return "timeline-background";
}

export function resolvePageMarkerPanSeek(params: {
  pointerX: number;
  dragStartPointerX: number;
  dragStartPageStart: number;
  pixelsPerSecond: number;
  pageDuration: number;
  totalDuration: number;
}): number {
  const pixelsPerSecond = Math.max(Number.EPSILON, params.pixelsPerSecond);
  const deltaSeconds = (params.pointerX - params.dragStartPointerX) / pixelsPerSecond;
  return clampTimelinePageStart(
    params.dragStartPageStart + deltaSeconds,
    params.pageDuration,
    params.totalDuration,
  );
}

export function resolveTimelinePageJumpSeek(params: {
  pointerX: number;
  xToTime: (x: number) => number;
  pageDuration: number;
  totalDuration: number;
  centerPageOnClick?: boolean;
}): number {
  const clickedTime = clampTimelineSeekSeconds(
    params.xToTime(params.pointerX),
    params.totalDuration,
  );
  const pageDuration = Math.max(0.1, params.pageDuration);
  const pageStart = params.centerPageOnClick
    ? clickedTime - pageDuration / 2
    : clickedTime;
  return clampTimelinePageStart(pageStart, pageDuration, params.totalDuration);
}

export function resolveTimelineScrubSeekSeconds(params: {
  pointerX: number;
  xToTime: (x: number) => number;
  totalDuration: number;
  findAnnotationAt?: (x: number) => TimelineAnnotation | null;
  snapToAnnotation?: boolean;
}): number {
  const {
    pointerX,
    xToTime,
    totalDuration,
    findAnnotationAt,
    snapToAnnotation = true,
  } = params;

  if (snapToAnnotation && findAnnotationAt) {
    const annotation = findAnnotationAt(pointerX);
    if (annotation) {
      return clampTimelineSeekSeconds(annotation.startTime, totalDuration);
    }
  }

  return clampTimelineSeekSeconds(xToTime(pointerX), totalDuration);
}

export function resolveTimelineOverviewPointerSeek(params: {
  pointerX: number;
  xToTime: (x: number) => number;
  pageStartX: number;
  pageEndX: number;
  pageDuration: number;
  totalDuration: number;
  findAnnotationAt?: (x: number) => TimelineAnnotation | null;
  snapToAnnotation?: boolean;
}): number {
  const target = hitTestTimelinePageMarker({
    pointerX: params.pointerX,
    pageStartX: params.pageStartX,
    pageEndX: params.pageEndX,
  });

  if (target === "page-marker") {
    return clampTimelinePageStart(
      params.xToTime(params.pageStartX),
      params.pageDuration,
      params.totalDuration,
    );
  }

  if (params.snapToAnnotation && params.findAnnotationAt) {
    const annotation = params.findAnnotationAt(params.pointerX);
    if (annotation) {
      return clampTimelinePageStart(
        annotation.startTime,
        params.pageDuration,
        params.totalDuration,
      );
    }
  }

  return resolveTimelinePageJumpSeek({
    pointerX: params.pointerX,
    xToTime: params.xToTime,
    pageDuration: params.pageDuration,
    totalDuration: params.totalDuration,
    centerPageOnClick: true,
  });
}
